import { z } from 'zod';

import { calendarDateSchema } from './employee';
import { RECORD_SORTS, listQuerySchema, sortParam } from './pagination';

/*
 * Jobs. reference/data-model.md §4, business-rules.md §3, ROADMAP.md §4.2.
 *
 * Statuses and their allowed transitions live in jobService — these schemas
 * validate shape, not lifecycle. A request to complete a pending job is
 * well-formed and refused by the service, so the error can say why in business
 * terms rather than "invalid input".
 */

export const JOB_STATUS_VALUES = [
  'pending',
  'scheduled',
  'in_progress',
  'completed',
  'cancelled',
] as const;

export type JobStatusValue = (typeof JOB_STATUS_VALUES)[number];

/** The display names. A raw `in_progress` must never reach a screen (P5). */
export const JOB_STATUS_LABELS: Record<JobStatusValue, string> = {
  pending: 'Pending',
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const trimmed = (max: number, label = 'Required') =>
  z
    .string()
    .trim()
    .min(1, label)
    .max(max, `Must be ${String(max)} characters or fewer`);

const optional = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Must be ${String(max)} characters or fewer`)
    .transform((value) => (value.length === 0 ? null : value))
    .nullable()
    .optional();

/**
 * A wall-clock time of day. The `<input type="time">` sends `HH:MM`; MySQL
 * hands back `HH:MM:SS`. Both are accepted and normalised to `HH:MM:SS`, so a
 * value can round-trip through an edit form without failing its own schema.
 */
export const wallClockTimeSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, 'Use a time like 09:00')
  .transform((value) => (value.length === 5 ? `${value}:00` : value));

const durationSchema = z
  .number()
  .int('Give whole minutes')
  .min(15, 'A job takes at least 15 minutes')
  .max(1440, 'A job cannot run longer than a day');

/**
 * Who to put on the job, in order — **the first is the lead** (ROADMAP.md
 * §4.5). The whole list each time, so removing someone is the same call as
 * adding someone.
 */
export const assignmentListSchema = z
  .array(z.uuid())
  .max(20, 'That is more people than the business employs');

export const createJobSchema = z.object({
  clientId: z.uuid(),

  /**
   * Required on manual creation even though the column is nullable — a cleaner
   * cannot drive to a job with no address, and `address_snapshot` is derived
   * from the property server-side. Future sources (calendar sync) carry their
   * own address and may skip it.
   */
  propertyId: z.uuid(),

  /** The checklist template to copy. Null is a job with no checklist. */
  categoryId: z.uuid().nullable().optional(),

  serviceTypeId: z.uuid(),

  scheduledDate: calendarDateSchema,
  scheduledTime: wallClockTimeSchema,
  estimatedDurationMinutes: durationSchema,

  specialInstructions: optional(2000),

  employeeIds: assignmentListSchema.optional(),
});

export type CreateJobInput = z.infer<typeof createJobSchema>;

/**
 * The fields an edit can touch. Deliberately not the schedule (reschedule is
 * its own action, so it is visible in the audit trail as what it is), not the
 * status (transitions go through their own endpoints), and not the category —
 * swapping the checklist template under a job that may have photos against its
 * tasks is not an edit, it is a different job.
 */
export const updateJobSchema = z.object({
  serviceTypeId: z.uuid().optional(),
  estimatedDurationMinutes: durationSchema.optional(),
  specialInstructions: optional(2000),
  isHighPriority: z.boolean().optional(),
});

export type UpdateJobInput = z.infer<typeof updateJobSchema>;

export const rescheduleJobSchema = z.object({
  scheduledDate: calendarDateSchema,
  scheduledTime: wallClockTimeSchema,
});

export type RescheduleJobInput = z.infer<typeof rescheduleJobSchema>;

/** Cancel, never delete — and a cancellation without a reason is a mystery. */
export const cancelJobSchema = z.object({
  reason: trimmed(2000, 'Say why the job is being cancelled'),
});

export const setAssignmentsSchema = z.object({
  employeeIds: assignmentListSchema,
});

/**
 * `completedAt` is the backdating seam — business-rules.md §3 rule 4. Absent
 * means "now". Present, it is an admin recording that the work happened
 * earlier, and the service audit-logs it as exactly that.
 */
export const completeJobSchema = z.object({
  completedAt: z.iso.datetime({ local: true }).optional(),
});

export const completeTaskSchema = z.object({
  /** `true` completes, `false` reopens a mis-ticked task. */
  isCompleted: z.boolean(),
});

/**
 * The fields beside the file on a photo upload. `uploadedByEmployeeId` names
 * who took it — the office attaches photos the crew sent in, and the record
 * says whose evidence it is, so the uploader must be on the job's crew.
 */
export const uploadPhotoSchema = z.object({
  jobTaskId: z.uuid().optional(),
  kind: z.enum(['before', 'after', 'issue']).default('after'),
  caption: optional(512),
  uploadedByEmployeeId: z.uuid(),
});

export const jobListQuerySchema = listQuerySchema.extend({
  sort: sortParam(RECORD_SORTS, 'newest'),

  status: z.enum([...JOB_STATUS_VALUES, 'all']).default('all'),
  clientId: z.uuid().optional(),
  employeeId: z.uuid().optional(),

  /**
   * The calendar's window. It fetches the range it can see and nothing more —
   * P8 is the prototype fetching `limit=1000` on every calendar view.
   */
  from: calendarDateSchema.optional(),
  to: calendarDateSchema.optional(),
});

export type JobListQuery = z.infer<typeof jobListQuerySchema>;

/**
 * What an assignment check wants to know before the admin commits to a crew:
 * who is free, who is double-booked, who is at their daily ceiling, and — from
 * 7.4 — how they rank and why.
 */
export const assignmentCheckQuerySchema = z.object({
  date: calendarDateSchema,
  time: wallClockTimeSchema,
  durationMinutes: z.coerce.number().int().min(15).max(1440),
  /** Editing an existing job's crew — its own assignments are not conflicts. */
  excludeJobId: z.uuid().optional(),

  /*
   * Both optional, and both only feed the *scoring* (business-rules.md §4).
   *
   * The create dialog knows them and the reassignment dialog can look them up,
   * but a check made before a client is chosen is still a legitimate question —
   * "who is free on Thursday morning". Where one is missing the criterion it
   * feeds simply does not appear among the reasons, rather than scoring zero
   * silently: a ranking that quietly stops counting something is exactly the
   * black box the explainability rule exists to prevent.
   */
  clientId: z.uuid().optional(),
  serviceTypeId: z.uuid().optional(),

  /**
   * Which property the job is at, for the same-area score — added 2026-08-07.
   *
   * Optional on the same terms as the two above and for the same reason: "who
   * is free on Thursday morning" is a legitimate question before an address is
   * chosen. Where it is missing the area criterion does not appear among the
   * reasons rather than scoring zero silently.
   *
   * **The property, not the client.** A client can have several addresses in
   * different places — a house in town and a rental up at Brian Head — and
   * scoring against their primary one would answer a question nobody asked.
   */
  propertyId: z.uuid().optional(),
});

export type AssignmentCheckQuery = z.infer<typeof assignmentCheckQuerySchema>;

// ---------------------------------------------------------------------------
// Views — what the API returns.
// ---------------------------------------------------------------------------

export interface JobAssigneeView {
  employeeId: string;
  name: string;
  role: 'lead' | 'cleaner' | 'support';
}

export interface JobListItem {
  id: string;
  jobNumber: string;
  status: JobStatusValue;
  /** The display value, never the slug. */
  statusLabel: string;

  clientId: string;
  clientName: string;

  propertyId: string | null;
  addressSnapshot: string;

  serviceTypeName: string;
  categoryName: string | null;

  scheduledDate: string;
  scheduledTime: string;
  estimatedDurationMinutes: number;

  isHighPriority: boolean;

  assignees: JobAssigneeView[];
}

export interface JobTaskView {
  id: string;
  name: string;
  sortOrder: number;
  isRequired: boolean;
  requiresPhoto: boolean;
  isCompleted: boolean;
  completedAt: string | null;
  completedByName: string | null;
  photoCount: number;
}

export interface JobPhotoView {
  id: string;
  jobTaskId: string | null;
  kind: 'before' | 'after' | 'issue';
  caption: string | null;
  uploadedByName: string;
  uploadedAt: string;
  width: number | null;
  height: number | null;
  /** A signed URL, minted for this response. Never stored anywhere. */
  url: string;
}

export interface JobHistoryView {
  id: string;
  fromStatus: JobStatusValue | null;
  toStatus: JobStatusValue;
  fromStatusLabel: string | null;
  toStatusLabel: string;
  changedByName: string | null;
  changedAt: string;
  note: string | null;
}

export interface JobDetail extends JobListItem {
  categoryId: string | null;
  serviceTypeId: string;
  specialInstructions: string | null;

  /** Admin always; employees only when assigned — business-rules.md §2. */
  propertyAccessNotes: string | null;

  source: 'manual' | 'request' | 'recurring' | 'calendar_sync';
  cancelledReason: string | null;

  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;

  tasks: JobTaskView[];
  photos: JobPhotoView[];
  history: JobHistoryView[];

  taskCounts: {
    total: number;
    completed: number;
    requiredTotal: number;
    requiredCompleted: number;
  };
}

/**
 * What the schedule says about this person on this date — ROADMAP.md §7.3.
 *
 * Resolved by `availabilityService`: an override wins outright, else the weekly
 * row for that day of week, else **unavailable**. The default being unavailable
 * is why `explanation` is a whole sentence rather than a flag — on a business
 * that has not filled the table in, every candidate reads as unavailable, and
 * "unavailable" alone leaves whoever is staffing the job with nowhere to go.
 */
export interface CandidateAvailability {
  isAvailable: boolean;
  startTime: string | null;
  endTime: string | null;
  /** "Tuesday is a day off on their weekly schedule." Always a full sentence. */
  explanation: string;
  source: 'override' | 'weekly' | 'none';

  /**
   * They work that day, but this job starts before they do or finishes after
   * they leave.
   *
   * A different sentence from "unavailable" because it has a different fix:
   * move the job, or extend the day. Lumping the two together would tell an
   * admin to change the wrong thing.
   */
  outsideWorkingHours: boolean;
}

/**
 * One line of the "why" behind a candidate's score — ROADMAP.md §7.4.
 *
 * **A whole sentence, and the points it was worth.** business-rules.md §4 says
 * the ranking has to be explainable "because the admin has to be able to
 * disagree with it", and an admin can only disagree with something stated. A
 * bare number is not a reason.
 *
 * `points` may be zero: "The job runs outside their hours" costs nothing under
 * the §4 table but is still worth saying, because it is why the +10 for being
 * available in the window is missing.
 */
export interface AssignmentReason {
  label: string;
  points: number;
}

/** One employee's row in the assignment dialog — warnings, not verdicts. */
export interface AssignmentCandidate {
  employeeId: string;
  name: string;
  /** Jobs already on their day. */
  jobsThatDay: number;
  maxJobsPerDay: number;
  /** True when one more job would breach the ceiling — a blocking condition. */
  atDailyCap: boolean;
  conflicts: {
    jobId: string;
    jobNumber: string;
    clientName: string;
    scheduledTime: string;
    estimatedDurationMinutes: number;
  }[];

  /**
   * **A warning here, a disqualifier in the ranking.** business-rules.md §4
   * makes "unavailable per schedule/override" a disqualifier for *auto*
   * assignment; nothing written stops an admin deliberately putting somebody on
   * a job outside their hours, and the office knows things the table does not.
   * So this is shown and not enforced — the hard refusals at write time stay an
   * inactive employee and `max_jobs_per_day`.
   */
  availability: CandidateAvailability;

  // -------------------------------------------------------------------------
  // The ranking — ROADMAP.md §7.4, business-rules.md §4
  // -------------------------------------------------------------------------

  /** The §4 table, added up. Meaningless on a disqualified candidate. */
  score: number;

  /**
   * A hard disqualifier applies: a time conflict, `max_jobs_per_day`, or
   * unavailable per schedule/override.
   *
   * Disqualified from the *automatic* suggestion, not from being picked. A
   * human can still tick the box and save, which is the whole shape of
   * "never auto-assign silently".
   */
  isDisqualified: boolean;

  /** Why, in words. Empty when nothing disqualifies them. */
  disqualifiers: string[];

  /** Every scored criterion that applied, biggest contribution first. */
  reasons: AssignmentReason[];
}
